import {
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
} from "@mui/material";
import Button from "@accredo/fe-shared/theme/components/Button.jsx";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { backendGet } from "@accredo/fe-shared/services/dataService.js";
import { useUser } from "@accredo/fe-shared/hooks/user.js";
import { useSnackbar } from "notistack";
import { useSubstitutionActions } from "../../hooks/useSubstitutionActions.jsx";
import Form from "@accredo/fe-shared/components/Form/Form.jsx";
import FormAutocomplete from "../Form/controls/FormAutocomplete.jsx";
import FormDateTimePicker from "../Form/controls/FormDateTimePicker.jsx";
import FormNumberField from "../Form/controls/FormNumberField.jsx";
import FormRadioGroup from "../Form/controls/FormRadioGroup.jsx";
import SubmitButton from "@accredo/fe-shared/components/Form/controls/SubmitButton.jsx";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

// convert local time to UTC for backend
dayjs.extend(utc);

const fetchUsers = async (owner_id) => {
  let users = [];
  await backendGet(`data/user/possible-substitutes/${owner_id}`, {
    setResult: (result) => {
      users = result.data;
    },
  });
  return users;
};

const shouldDisableTime = (value, view) => {
  const dayjsValue = dayjs(value);
  const allowedHours = [0, 8, 12, 16, 23];

  if (view === "hours") {
    // Disable all hours not in the allowed list
    return !allowedHours.includes(dayjsValue.hour());
  }

  if (view === "minutes") {
    const hour = dayjsValue.hour();
    // For hour 23, only allow minute 59
    if (hour === 23) {
      return dayjsValue.minute() !== 59;
    }
    // For all other allowed hours, only allow minute 0
    if (allowedHours.includes(hour)) {
      return dayjsValue.minute() !== 0;
    }
    // Disable all minutes for any other hour (shouldn't be reachable)
    return true;
  }

  return false;
};

const userSelectionSchema = yup.object({
  user_id: yup
    .mixed()
    .required("Benutzer ist erforderlich")
    .test(
      "is-valid-user",
      "Benutzer ist erforderlich",
      (value) => value && typeof value === "object" && value.id
    ),
});

const substitutionSchema = yup.object({
  substitute_user_id: yup
    .number("Bitte wählen Sie eine Stellvertretung aus.")
    .required("Bitte wählen Sie eine Stellvertretung aus."),
  notification_mode: yup.string().oneOf(["redirect", "copy"]).required(),
  starts_at: yup
    .date()
    .nullable()
    .typeError("Bitte geben Sie ein gültiges Startdatum ein.")
    .test(
      "valid-date",
      "Bitte geben Sie ein gültiges Startdatum ein.",
      (value) => {
        if (!value) return true; // Allow null/empty
        return dayjs(value).isValid();
      }
    ),
  ends_at: yup
    .date()
    .nullable()
    .typeError("Bitte geben Sie ein gültiges Enddatum ein.")
    .test(
      "valid-date",
      "Bitte geben Sie ein gültiges Enddatum ein.",
      (value) => {
        if (!value) return true; // Allow null/empty
        return dayjs(value).isValid();
      }
    )
    .when("starts_at", (starts_at, schema) => {
      if (starts_at && dayjs.isDayjs(starts_at) && starts_at.isValid()) {
        return schema.min(
          starts_at.toDate(),
          "Das Enddatum muss nach dem Beginndatum liegen."
        );
      }
      return schema;
    }),
  notification_grace_period_start_days: yup
    .number()
    .transform((value, original) => (original === "" ? 0 : value))
    .min(0, "Wert muss mindestens 0 sein.")
    .max(30, "Wert darf höchstens 30 sein.")
    .default(0),
  notification_grace_period_end_days: yup
    .number()
    .transform((value, original) => (original === "" ? 0 : value))
    .min(0, "Wert muss mindestens 0 sein.")
    .max(30, "Wert darf höchstens 30 sein.")
    .default(0),
  access_grace_period_start_days: yup
    .number()
    .transform((value, original) => (original === "" ? 0 : value))
    .min(0, "Wert muss mindestens 0 sein.")
    .max(30, "Wert darf höchstens 30 sein.")
    .default(0),
  access_grace_period_end_days: yup
    .number()
    .transform((value, original) => (original === "" ? 0 : value))
    .min(0, "Wert muss mindestens 0 sein.")
    .max(30, "Wert darf höchstens 30 sein.")
    .default(0),
});

const SubstitutionModal = ({
  open,
  onClose,
  onSuccess = () => {},
  substitution = null,
  isEdit = false,
}) => {
  const { user } = useUser();
  const canSelectSubstitutee =
    user?.role_names?.includes("sysadmin") ||
    user?.role_names?.includes("kd_verwaltung");

  const [step, setStep] = useState(canSelectSubstitutee ? 1 : 2);
  const [selectedUser, setSelectedUser] = useState(
    canSelectSubstitutee ? null : user
  );
  const { createSubstitution, updateSubstitution } = useSubstitutionActions();
  const { enqueueSnackbar } = useSnackbar();

  // Reset step when modal closes
  useEffect(() => {
    if (!open) {
      setStep(canSelectSubstitutee ? 1 : 2);
      setSelectedUser(canSelectSubstitutee ? null : user);
    }
  }, [open, canSelectSubstitutee, user]);

  // Skip to step 2 for edit mode or when user can only substitute themselves
  useEffect(() => {
    if (open) {
      if (isEdit && substitution) {
        setStep(2);
        setSelectedUser(substitution.user);
      } else if (!isEdit) {
        if (canSelectSubstitutee) {
          setStep(1);
        } else {
          setSelectedUser(user);
          setStep(2);
        }
      }
    }
  }, [isEdit, substitution, open, canSelectSubstitutee, user]);

  const handleStep1Submit = (data) => {
    setSelectedUser(data.user_id);
    setStep(2);
  };

  const handleStep2Submit = async (data, formMethods) => {
    try {
      const submission_data = {
        substitute_user_id: data.substitute_user_id,
        starts_at: data.starts_at
          ? dayjs(data.starts_at).utc().format("YYYY-MM-DD HH:mm:ss")
          : null,
        ends_at: data.ends_at
          ? dayjs(data.ends_at).utc().format("YYYY-MM-DD HH:mm:ss")
          : null,
        notification_mode: data.notification_mode,
        notification_grace_period_start_days:
          data.notification_grace_period_start_days ?? 0,
        notification_grace_period_end_days:
          data.notification_grace_period_end_days ?? 0,
        access_grace_period_start_days:
          data.access_grace_period_start_days ?? 0,
        access_grace_period_end_days: data.access_grace_period_end_days ?? 0,
      };

      if (isEdit && substitution) {
        await updateSubstitution(
          substitution.user.id,
          substitution.id,
          submission_data
        );
      } else {
        await createSubstitution(selectedUser.id, submission_data);
      }

      onSuccess();
      onClose();
    } catch (error) {
      if (error.response && error.response.status === 422) {
        const validation_errors = error.response.data.errors;
        Object.entries(validation_errors).forEach(([field, messages]) => {
          formMethods.setError(field, {
            type: "server",
            message: messages[0],
          });
        });
      } else {
        enqueueSnackbar("Ein unerwarteter Fehler ist aufgetreten.", {
          variant: "error",
        });
        console.error("Submission failed:", error);
      }
    }
  };

  const handleBack = () => {
    setStep(1);
  };

  const fetchSubstitutableUsers = async () => {
    let users = [];
    await backendGet("data/user/substitutable-users", {
      setResult: (result) => {
        users = result.data;
      },
    });
    return users;
  };

  const selectedUserName = selectedUser?.full_name
    ? selectedUser?.full_name
    : `${selectedUser?.title || ""} ${selectedUser?.vorname || ""} ${selectedUser?.nachname || ""}`.trim();

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {isEdit
          ? "Stellvertretung bearbeiten"
          : step === 1
            ? "Benutzer auswählen"
            : `Stellvertretung für ${selectedUserName || ""} erstellen`}
      </DialogTitle>
      {step === 1 ? (
        // Step 1: User Selection
        <Form
          onSubmit={handleStep1Submit}
          defaultValues={{ user_id: null }}
          resolver={yupResolver(userSelectionSchema)}
        >
          <DialogContent>
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 1 }}
            >
              <FormAutocomplete
                name="user_id"
                label="Benutzer (wird vertreten)"
                fetchOptions={fetchSubstitutableUsers}
                getOptionLabel={(option) => `${option.full_name}`}
                transformValue={(option) => option}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ paddingX: 3, paddingBottom: 2 }}>
            <Button variant="tertiary" onClick={onClose}>
              Abbrechen
            </Button>
            <SubmitButton>Weiter</SubmitButton>
          </DialogActions>
        </Form>
      ) : (
        // Step 2: Substitution Form
        <Form
          key={isEdit && substitution ? substitution.id : "new"}
          onSubmit={handleStep2Submit}
          defaultValues={{
            substitute_user_id: substitution?.substitute?.id || null,
            notification_mode: substitution?.notification_mode || "redirect",
            starts_at: substitution?.starts_at
              ? dayjs(substitution.starts_at)
              : null,
            ends_at: substitution?.ends_at ? dayjs(substitution.ends_at) : null,
            notification_grace_period_start_days:
              substitution?.notification_grace_period_start_days ?? 0,
            notification_grace_period_end_days:
              substitution?.notification_grace_period_end_days ?? 0,
            access_grace_period_start_days:
              substitution?.access_grace_period_start_days ?? 0,
            access_grace_period_end_days:
              substitution?.access_grace_period_end_days ?? 0,
          }}
          resolver={yupResolver(substitutionSchema)}
        >
          <DialogContent>
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 1 }}
            >
              {!isEdit && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Benutzer: {selectedUserName}
                </Typography>
              )}

              <FormAutocomplete
                name="substitute_user_id"
                label="Stellvertreter"
                fetchOptions={() => fetchUsers(selectedUser?.id)}
                getOptionLabel={(option) => `${option.full_name}`}
              />

              <Box sx={{ display: "flex", gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <FormDateTimePicker
                    name="starts_at"
                    label="Beginn (optional)"
                    ampm={false}
                    shouldDisableTime={shouldDisableTime}
                    timeSteps={{ minutes: 1 }}
                    skipDisabled={true}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <FormDateTimePicker
                    name="ends_at"
                    label="Ende (optional)"
                    ampm={false}
                    shouldDisableTime={shouldDisableTime}
                    timeSteps={{ minutes: 1 }}
                    minutesStep={1}
                    skipDisabled={true}
                  />
                </Box>
              </Box>

              <FormRadioGroup
                name="notification_mode"
                label="Benachrichtigungen & Mails"
                options={[
                  { value: "redirect", label: "Umleiten" },
                  { value: "copy", label: "Kopieren" },
                ]}
              />

              <Box sx={{ display: "flex", gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <FormNumberField
                    name="notification_grace_period_start_days"
                    label="Karenz Beginn (Arbeitstage)"
                    helperText="Tage vor Beginn mit Vorab-Benachrichtigungen für den Stellvertreter."
                    infoText="Anzahl der Arbeitstage vor Beginn, in denen der Stellvertreter bereits eine Kopie der Benachrichtigungen des vertretenen Benutzers erhält, um sich vor der Übernahme einzuarbeiten."
                  />
                </Box>

                <Box sx={{ flex: 1 }}>
                  <FormNumberField
                    name="notification_grace_period_end_days"
                    label="Karenz Ende (Arbeitstage)"
                    helperText="Tage vor Ende mit zusätzlichen Benachrichtigungen für den vertretenen Benutzer (nur bei „Umleiten“)."
                    infoText="Anzahl der Arbeitstage vor Ende, in denen der vertretene Benutzer zusätzlich zum Stellvertreter wieder Benachrichtigungen erhält (nur bei Modus „Umleiten“)."
                  />
                </Box>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  Zugriff auf Schaden
                </Typography>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <FormNumberField
                      name="access_grace_period_start_days"
                      label="Zugriffskarenz vor Beginn (Arbeitstage)"
                      helperText="Tage vor Beginn mit vorgezogenem Zugriff des Stellvertreters."
                      infoText="Anzahl der Arbeitstage vor Beginn der Stellvertretung, in denen der Stellvertreter bereits auf die Schäden des vertretenen Benutzers zugreifen kann."
                    />
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <FormNumberField
                      name="access_grace_period_end_days"
                      label="Zugriffskarenz nach Ende (Arbeitstage)"
                      helperText="Tage nach Ende mit fortgesetztem Zugriff des Stellvertreters."
                      infoText="Anzahl der Arbeitstage nach Ende der Stellvertretung, in denen der Stellvertreter weiterhin auf die Schäden des vertretenen Benutzers zugreifen kann (z. B. über einen Benachrichtigungslink)."
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ paddingX: 3, paddingBottom: 2 }}>
            {step === 2 && !isEdit && canSelectSubstitutee && (
              <Button variant="tertiary" onClick={handleBack}>
                Zurück
              </Button>
            )}
            <Button variant="tertiary" onClick={onClose}>
              Abbrechen
            </Button>
            <SubmitButton>{isEdit ? "Speichern" : "Erstellen"}</SubmitButton>
          </DialogActions>
        </Form>
      )}
    </Dialog>
  );
};

SubstitutionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  substitution: PropTypes.object,
  isEdit: PropTypes.bool,
};

export default SubstitutionModal;
